import { useState, useCallback, useEffect } from "react";
import WebApp from "@twa-dev/sdk";
// Add type declaration for Telegram

interface TelegramLoginOptions {
  bot_id: string;
  request_access?: boolean;
  embed?: boolean;
}

interface TelegramLoginCallbackData {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

declare global {
  interface Window {
    Telegram?: {
      LoginWidget?: {
        auth: (
          options: TelegramLoginOptions,
          callback: (data: TelegramLoginCallbackData | false) => void
        ) => void;
      };
      Login: {
        auth: (
          options: {
            bot_id: string | undefined;
            request_access: boolean;
            embed: boolean;
          },
          callback: (data: any) => void
        ) => void;
      };
      WebApp?: any;
    };
    gtag?: (...args: any[]) => void;
  }
}

// Add TelegramUser type
interface TelegramUser {
  id: number;
  first_name: string;
  username?: string;
  photo_url?: string;
}

function mapToTelegramUser(
  data: TelegramLoginCallbackData | TelegramUser
): TelegramUser {
  return {
    id: data.id,
    first_name: data.first_name,
    username: data.username,
    photo_url: data.photo_url,
  };
}

export function useTelegram() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<TelegramUser | null>(null);
  const tg = window.Telegram?.WebApp;
  // Initialize from WebApp data
  useEffect(() => {
    try {
      // Check if we're in Telegram WebApp environment
      if (WebApp.initData) {
        const twaUser = WebApp.initDataUnsafe.user;
        if (twaUser) {
          const telegramUser: TelegramUser = {
            id: twaUser.id,
            first_name: twaUser.first_name,
            username: twaUser.username,
            photo_url: twaUser.photo_url,
          };
          setUser(telegramUser);
          setIsAuthenticated(true);
          localStorage.setItem("telegramUser", JSON.stringify(telegramUser));
          return;
        }
      }

      // Fallback to localStorage
      const storedUser = localStorage.getItem("telegramUser");
      if (storedUser) {
        setUser(JSON.parse(storedUser));
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error("Error initializing Telegram user:", error);
    }
  }, []);

  const login = useCallback(() => {
    console.log("Callback called, bot id:", process.env.TELEGRAM_BOT_ID);
    if (!window.Telegram) {
      console.error("Telegram SDK not loaded");
      return;
    }

    // ✅ 1. Priority - LoginWidget
    if (window.Telegram.LoginWidget?.auth) {
      console.log("Using login widget")
      window.Telegram.LoginWidget.auth(
        {
          bot_id: process.env.TELEGRAM_BOT_ID!,
          request_access: true,
        },
        data => {
          console.log("Data:", data)
          if (!data) return;
        
          const telegramUser = mapToTelegramUser(data);

          setUser(telegramUser);
          setIsAuthenticated(true);
          localStorage.setItem("telegramUser", JSON.stringify(telegramUser));
        }
      );
      return;
    }

    // ⚠️ 2. Fallback — deprecated Login
    if (window.Telegram.Login?.auth) {
      window.Telegram.Login.auth(
        {
          bot_id: process.env.TELEGRAM_BOT_ID,
          request_access: true,
          embed: false,
        },
        data => {
          if (!data) return;

          const telegramUser = mapToTelegramUser(data);

          setUser(telegramUser);
          setIsAuthenticated(true);
          localStorage.setItem("telegramUser", JSON.stringify(telegramUser));
        }
      );
    }
  }, []);

  const logout = useCallback(() => {
    setIsAuthenticated(false);
    setUser(null);
    localStorage.removeItem("telegramUser");
  }, []);

  return { isAuthenticated, login, logout, user };
}
